import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useApp, useT } from "@/context/AppContext";
import { Mail, Lock, Sparkles, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const SplitLayout = ({ children }) => (
  <div className="min-h-[calc(100vh-80px)] grid lg:grid-cols-2" data-testid="auth-layout">
    <div className="flex items-center justify-center p-8 sm:p-12">
      <div className="w-full max-w-sm">{children}</div>
    </div>
    <div className="hidden lg:block relative grain-bg overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#1F3A2E]/90 via-[#1F3A2E]/70 to-[#2d4a3e]/50" />
      <img src="/images/qiox/image8-35.png" alt="kiosk" className="absolute inset-0 w-full h-full object-contain p-20" />
      <div className="absolute bottom-12 left-12 right-12 text-[#FDFBF7]">
        <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#D97742]">Kiosk Configurator</div>
        <div className="display text-3xl md:text-4xl font-semibold mt-2">Van branche naar kiosk in 5 minuten</div>
      </div>
    </div>
  </div>
);

export function LoginPage() {
  const t = useT();
  const { login } = useApp();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await login(email, password); toast.success("Ingelogd"); nav(redirect); }
    catch (e2) { setErr(formatApiError(e2.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <SplitLayout>
      <h1 className="display text-3xl font-semibold text-[#F4F1EA]">{t.auth.login_title}</h1>
      <p className="text-sm text-[#A8B3AD] mt-2">{t.auth.have_account}</p>
      <form className="mt-8 space-y-4" onSubmit={submit} data-testid="login-form">
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#6FAA8A]">{t.auth.email}</span>
          <div className="relative mt-1">
            <Mail className="absolute left-3 top-3 w-4 h-4 text-[#A8B3AD]" />
            <input className="input-field pl-10" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
          </div>
        </label>
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#6FAA8A]">{t.auth.password}</span>
          <div className="relative mt-1">
            <Lock className="absolute left-3 top-3 w-4 h-4 text-[#A8B3AD]" />
            <input className="input-field pl-10" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
          </div>
        </label>
        {err && <div className="text-sm text-[#FF6B5B]" data-testid="login-error">{err}</div>}
        <button className="btn-primary w-full justify-center" disabled={loading} data-testid="login-submit-button">
          {loading ? "..." : t.auth.submit_login}
        </button>
        <div className="text-xs text-center">
          <Link to="/forgot-password" className="text-[#A8B3AD] hover:text-[#6FAA8A]" data-testid="forgot-password-link">{t.auth.forgot}</Link>
        </div>
        <div className="text-sm text-center text-[#A8B3AD]">
          {t.auth.no_account} <Link to="/register" className="text-[#6FAA8A] font-semibold" data-testid="go-register-link">{t.auth.register_title}</Link>
        </div>
      </form>
    </SplitLayout>
  );
}

export function RegisterPage() {
  const t = useT();
  const { register } = useApp();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "", company: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [devLink, setDevLink] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      const data = await register(form);
      setDevLink(data.verification_link_dev || "");
      toast.success("Account aangemaakt! Bevestig je e-mail.");
      setTimeout(() => nav("/verify-email"), 600);
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <SplitLayout>
      <h1 className="display text-3xl font-semibold text-[#F4F1EA]">{t.auth.register_title}</h1>
      <p className="text-sm text-[#A8B3AD] mt-2">Begin met configureren in 30 seconden.</p>
      <form className="mt-6 space-y-3" onSubmit={submit} data-testid="register-form">
        <Input label={t.auth.name} value={form.name} onChange={(v) => setForm({ ...form, name: v })} id="register-name-input" required />
        <Input label={t.auth.company} value={form.company} onChange={(v) => setForm({ ...form, company: v })} id="register-company-input" />
        <Input label={t.auth.email} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} id="register-email-input" required />
        <Input label={t.auth.password} type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} id="register-password-input" required />
        {err && <div className="text-sm text-[#FF6B5B]" data-testid="register-error">{err}</div>}
        {devLink && (
          <a href={devLink} className="block text-xs text-[#D97742] underline" data-testid="register-dev-link">
            <ExternalLink className="w-3 h-3 inline mr-1" /> {t.auth.dev_link}: {devLink}
          </a>
        )}
        <button className="btn-primary w-full justify-center" disabled={loading} data-testid="register-submit-button">
          <Sparkles className="w-4 h-4" /> {loading ? "..." : t.auth.submit_register}
        </button>
        <div className="text-sm text-center text-[#A8B3AD]">
          {t.auth.have_account} <Link to="/login" className="text-[#6FAA8A] font-semibold" data-testid="go-login-link">{t.auth.login_title}</Link>
        </div>
      </form>
    </SplitLayout>
  );
}

export function VerifyEmailPage() {
  const t = useT();
  const { user, setUser } = useApp();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(params.get("token") ? "loading" : "idle");
  const [devMails, setDevMails] = useState([]);

  useEffect(() => {
    const token = params.get("token");
    if (token) {
      api.post("/auth/verify-email", { token })
        .then(() => { setStatus("ok"); setUser(user ? { ...user, email_verified: true } : user); toast.success(t.auth.verified); })
        .catch(() => setStatus("error"));
    }
  }, []);

  useEffect(() => {
    if (user?.email) {
      api.get(`/dev/mock-outbox`, { params: { email: user.email } }).then((r) => setDevMails(r.data));
    }
  }, [user?.email]);

  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend-verification");
      if (data?.verification_link_dev) {
        toast.success("Opnieuw verstuurd (mock): " + data.verification_link_dev);
        setDevMails((m) => [...m, { subject: "Confirm", body: data.verification_link_dev }]);
      }
    } catch { toast.error("Fout bij opnieuw versturen"); }
  };

  return (
    <SplitLayout>
      <h1 className="display text-3xl font-semibold text-[#F4F1EA]">{t.auth.verify_title}</h1>
      <p className="text-sm text-[#A8B3AD] mt-2">{t.auth.verify_sub}</p>

      {status === "ok" && (
        <div className="mt-6 p-4 rounded-xl bg-[#6FAA8A]/10 border border-[#6FAA8A]/25 flex items-center gap-3" data-testid="verify-success">
          <CheckCircle2 className="w-5 h-5 text-[#6FAA8A]" />
          <div className="text-sm text-[#6FAA8A] font-medium">{t.auth.verified}</div>
        </div>
      )}
      {status === "error" && (
        <div className="mt-6 p-4 rounded-xl bg-[#FF6B5B]/10 border border-[#FF6B5B]/25 text-sm text-[#FF6B5B]" data-testid="verify-error">
          Ongeldige of verlopen link.
        </div>
      )}

      {user && !user.email_verified && (
        <button className="btn-outline mt-6" onClick={resend} data-testid="resend-verification-button">
          Nieuwe link versturen
        </button>
      )}

      {devMails.length > 0 && (
        <div className="mt-8 card-surface p-4" data-testid="dev-mailbox">
          <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#D97742] mb-2">Dev mailbox (mock)</div>
          {devMails.slice(-3).map((m, i) => (
            <div key={i} className="text-xs py-2 border-b last:border-0 border-[#243430]">
              <div className="font-semibold">{m.subject}</div>
              <div className="text-[#A8B3AD] break-all">{m.body}</div>
            </div>
          ))}
        </div>
      )}
    </SplitLayout>
  );
}

export function ForgotPasswordPage() {
  const t = useT();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      setDevLink(data?.reset_link_dev || "");
      toast.success("Email verstuurd (mock)");
    } catch { toast.error("Fout"); }
  };

  return (
    <SplitLayout>
      <h1 className="display text-3xl font-semibold text-[#F4F1EA]">{t.auth.forgot}</h1>
      <form className="mt-6 space-y-4" onSubmit={submit} data-testid="forgot-form">
        <Input label={t.auth.email} type="email" value={email} onChange={setEmail} id="forgot-email-input" required />
        <button className="btn-primary w-full justify-center" data-testid="forgot-submit-button">Verstuur reset link</button>
        {sent && devLink && (
          <a href={devLink} className="block text-xs text-[#D97742] underline" data-testid="forgot-dev-link">
            <ExternalLink className="w-3 h-3 inline mr-1" /> Dev link: {devLink}
          </a>
        )}
      </form>
    </SplitLayout>
  );
}

export function ResetPasswordPage() {
  const t = useT();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Wachtwoord gereset!");
      nav("/login");
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };

  return (
    <SplitLayout>
      <h1 className="display text-3xl font-semibold text-[#F4F1EA]">{t.auth.reset_title}</h1>
      <form className="mt-6 space-y-4" onSubmit={submit} data-testid="reset-form">
        <Input label={t.auth.new_password} type="password" value={pw} onChange={setPw} id="reset-password-input" required />
        <button className="btn-primary w-full justify-center" disabled={!token || !pw} data-testid="reset-submit-button">{t.auth.reset_submit}</button>
      </form>
    </SplitLayout>
  );
}

function Input({ label, value, onChange, type = "text", id, required }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-[#6FAA8A]">{label}</span>
      <input className="input-field mt-1" type={type} value={value} required={required}
        onChange={(e) => onChange(e.target.value)} data-testid={id} />
    </label>
  );
}
